import PropTypes from "prop-types";
import { Handle, Position, NodeResizer, useInternalNode } from "@xyflow/react";
import { useMemo } from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useGetDialDataQuery } from "@/redux/api/dashboardApi";
import { useLiveDataWindow } from "../utils/useLiveDataWindow";
import { getComponentMeta } from "../registry/componentRegistry";

const getStatusText = (type, isRunning, isOffline) => {
  if (isOffline) return "OFFLINE";
  if (isRunning) {
    switch (type) {
      case "switchgear":
        return "CLOSED";
      case "valve":
        return "OPEN";
      case "msb":
        return "ENERGIZED";
      case "transformer":
        return "ACTIVE";
      case "chiller":
      case "splitACIndoor":
      case "split_ac":
        return "COOLING";
      case "highbayLamp":
      case "highbay_lamp":
        return "ON";
      case "solarPanel":
      case "solar_panel":
        return "GENERATING";
      case "solarInverter":
      case "solar_inverter":
        return "CONVERTING";
      case "battery":
        return "CHARGING";
      case "tanks":
      case "tank":
        return "ONLINE";
      default:
        return "RUNNING";
    }
  } else {
    switch (type) {
      case "switchgear":
        return "OPEN";
      case "valve":
        return "CLOSED";
      case "highbayLamp":
      case "highbay_lamp":
      case "splitACIndoor":
      case "split_ac":
      case "transformer":
      case "msb":
      case "solarInverter":
      case "solar_inverter":
      case "battery":
        return "OFF";
      case "solarPanel":
      case "solar_panel":
        return "IDLE";
      case "tanks":
      case "tank":
        return "OFF";
      default:
        return "STOPPED";
    }
  }
};

/**
 * BaseNode – shared wrapper for all SCADA node types.
 *
 * Provides:
 * - Consistent handle placement (input left, output right)
 * - Title label rendering (always directly below the node)
 * - Live device data fetching & display
 * - Selection border styling
 * - Rotation support
 * - Polling for real-time updates (10s interval)
 * - Exposes liveValue to children via render-prop pattern
 *
 * NOTE: Visualization widgets (gauge/graph/value) are now rendered as
 * separate floating VisualizationWidgetNode nodes on the canvas, NOT
 * inside the BaseNode. This prevents overlap with the component title
 * and matches SCADA/HMI instrumentation patterns.
 */
const BaseNode = ({ id, data, selected, children, width, height }) => {
  const node = useInternalNode(id);
  const componentMeta = getComponentMeta(data?.type);
  const defaultW = componentMeta?.defaultWidth ?? 80;
  const defaultH = componentMeta?.defaultHeight ?? 80;

  // Use reactive width & height from props or internal node state (fallback to component natural default)
  const rawW = width ?? node?.measured?.width ?? node?.width ?? (typeof node?.style?.width === 'number' ? node?.style?.width : parseFloat(node?.style?.width));
  const rawH = height ?? node?.measured?.height ?? node?.height ?? (typeof node?.style?.height === 'number' ? node?.style?.height : parseFloat(node?.style?.height));

  const currentWidth = (rawW && rawW !== 80) ? rawW : (rawW === 80 && defaultW !== 80 ? defaultW : (rawW ?? defaultW));
  const currentHeight = (rawH && rawH !== 80) ? rawH : (rawH === 80 && defaultH !== 80 ? defaultH : (rawH ?? defaultH));
  // Use currentWidth as reference dimension so tall components (like Tank) match standard 80px component title scale
  const nodeSize = currentWidth;

  const rotation = data?.rotation || 0;
  const title = data?.title || "";
  const config = data?.config || {};
  const deviceId = data?.deviceId;
  const parameterId = data?.parameterId;
  const showLabel = config.showLabel !== false; // default true
  const showStatus = config.showStatus !== false; // default true
  const labelFontSize = Number(config.fontSize || data?.styles?.fontSize || data?.fontSize || 14);

  const { dateRange } = useSelector((state) => state.dashboardV2 || { dateRange: {} });
  const liveWindow = useLiveDataWindow();

  // Build API params for live data fetch
  const params = useMemo(() => {
    if (!deviceId || !parameterId) return null;
    const date = liveWindow.value;
    return {
      ...date,
      device: deviceId,
      peakHours: dateRange?.peakHours,
      deviceParameter: JSON.stringify({ _id_in: parameterId, populate: "systemFormula systemParameter device" }),
    };
  }, [deviceId, parameterId, dateRange, liveWindow]);

  // Fetch live data with 10-second polling
  const { data: apiData, isFetching } = useGetDialDataQuery(params, {
    skip: !params,
    pollingInterval: 10000,
  });


  // Extract the live value and validate 12-hour window
  const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
  const rawValue = apiData?.data?.value;
  const rawDate = apiData?.data?.date;
  const dataTimestamp = rawDate ? new Date(rawDate).getTime() : null;
  const isWithin12Hours = dataTimestamp !== null && !Number.isNaN(dataTimestamp) && (Date.now() - dataTimestamp <= TWELVE_HOURS_MS);

  const hasValidValue = rawValue !== undefined && rawValue !== null && rawValue !== "" && rawValue !== "--" && !Number.isNaN(Number(rawValue));
  const hasLiveData = hasValidValue && isWithin12Hours;
  const unit = config.unit || apiData?.unit || "";

  // Device is OFFLINE if:
  // - No device bound (!deviceId)
  // - Or no data over 12 hours (!hasLiveData)
  // - Or backend explicitly flags device as inactive
  const isOffline = !deviceId || !hasLiveData || (apiData && apiData.deviceStatus === "inactive");
  const minL = config.minLevel ?? (config.minValue ?? 0);
  const maxL = config.maxLevel ?? (config.maxValue ?? 100);
  const valNum = hasLiveData ? Number(rawValue) : 0;

  // Stopped only when online with valid data within 12h, and data value is below or equal to min value
  const isRunning = !isOffline && hasLiveData && valNum > minL;

  const statusColor = isOffline
    ? "grey"
    : (isRunning ? "green" : "red");

  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
  const statusText = getStatusText(data?.type, isRunning, isOffline);

  // Normalized percentage ratio (0.0 to 1.0) strictly based on configured min and max
  const ratio = isRunning
    ? (maxL > minL ? Math.min(1, Math.max(0, (valNum - minL) / (maxL - minL))) : 1)
    : 0;

  // Proportional animation speed: scales smoothly from 0.4 (at min) to 2.2 (at max)
  const speedScale = isRunning ? 0.4 + ratio * 1.8 : 0;

  // Render the children — if it's a function, pass states down
  const renderedChildren = typeof children === "function"
    ? children({
        liveValue: hasLiveData ? valNum : null,
        unit,
        isFetching,
        isOffline,
        isRunning,
        statusColor,
        speedScale,
        ratio,
        designType: data?.styles?.designType || "realistic",
        color: data?.styles?.color || componentMeta?.color || "#3b82f6",
        opacity: data?.styles?.opacity ?? 1,
        width: currentWidth,
        height: currentHeight,
        config,
      })
    : children;

  const designType = data?.styles?.designType || "realistic";
  const isRealistic = designType === "realistic";
  const hasLink = Boolean(
    (config?.linkDashboardId && String(config.linkDashboardId).trim() !== "") ||
    (config?.linkTabId && String(config.linkTabId).trim() !== "") ||
    (data?.linkDashboardId && String(data.linkDashboardId).trim() !== "") ||
    (data?.linkTabId && String(data.linkTabId).trim() !== "") ||
    (config?.linkTargetType && config?.linkDashboardId)
  );

  return (
    <div
      className={`live-node ${selected ? "live-node--selected" : ""} ${hasLink ? "live-node--linked" : ""}`}
      style={{
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        width: currentWidth,
        height: currentHeight,
        cursor: data?.readOnly && hasLink ? "pointer" : undefined,
      }}
      title={title}
    >
      <NodeResizer
        isVisible={selected && !data?.readOnly}
        minWidth={40}
        minHeight={40}
        lineClassName="live-node__resize-line"
        handleClassName="live-node__resize-handle"
      />

      {/* SVG / Component content from child node */}
      <div className="live-node__body">{renderedChildren}</div>

      {/* Input handle – left side */}
      <Handle
        type="source"
        position={Position.Left}
        id="input"
        isConnectable={!data?.readOnly}
        isConnectableStart={!data?.readOnly}
        isConnectableEnd={!data?.readOnly}
        className="live-node__handle live-node__handle--input"
      />


      {/* Linked Dashboard indicator fallback (only if title label is hidden) */}
      {hasLink && (!showLabel || !title) && (
        <div
          className="live-node__link-indicator"
          title={data?.readOnly ? "Linked Dashboard (Click to open)" : "Linked to another dashboard"}
        >
          <FaExternalLinkAlt />
        </div>
      )}

      {/* SCADA Status Dot (Pilot LED) – Only for realistic design (matches top-right pilot light in other two styles) */}
      {isRealistic && showStatus && (
        <div
          className="live-node__scada-dot"
          style={{
            backgroundColor: dotColor,
            boxShadow: isRunning ? `0 0 5px ${dotColor}` : "0 1px 2px rgba(0,0,0,0.25)",
            animation: isRunning ? "scada-led-blink 1.8s infinite ease-in-out" : "none",
          }}
          title={`Status: ${statusText}`}
        />
      )}

      {/* SCADA Status Text – Only for realistic design (since SVG classic/glassmorphic already has it internally) */}
      {isRealistic && showStatus && (
        <div 
          className="live-node__scada-status"
          style={{
            position: "absolute",
            top: "calc(100% + 2px)",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: `${Math.max(6.5, Math.min(9.5, (nodeSize / 80) * 7.5)).toFixed(1)}px`,
            fontWeight: 800,
            letterSpacing: "0.5px",
            color: dotColor,
            textTransform: "uppercase",
            lineHeight: 1,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 2,
          }}
        >
          {statusText}
        </div>
      )}

      {/* Title label – default 14px for uniform appearance across all items, customizable via component Visualization config */}
      {showLabel && title && (
        <div 
          className="live-node__title"
          style={{ 
            fontSize: `${labelFontSize}px`,
            top: (isRealistic && showStatus)
              ? "calc(100% + 15px)"
              : "calc(100% + 6px)",
            bottom: "auto",
            padding: `${Math.max(2, Math.round(labelFontSize * 0.15))}px ${Math.max(6, Math.round(labelFontSize * 0.45))}px`,
            borderRadius: "4px",
          }}
        >
          <div className="fw-medium d-flex align-items-center gap-1">
            <span>{title}</span>
            {hasLink && (
              <FaExternalLinkAlt
                className="live-node__title-link-icon"
                title={data?.readOnly ? "Linked Dashboard (Click to open)" : "Linked to another dashboard"}
              />
            )}
          </div>
        </div>
      )}

      {/* Output handle – right side */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        isConnectable={!data?.readOnly}
        isConnectableStart={!data?.readOnly}
        isConnectableEnd={!data?.readOnly}
        className="live-node__handle live-node__handle--output"
      />
    </div>
  );
};

BaseNode.propTypes = {
  data: PropTypes.shape({
    rotation: PropTypes.number,
    title: PropTypes.string,
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    config: PropTypes.object,
    styles: PropTypes.object,
    deviceId: PropTypes.string,
    parameterId: PropTypes.string,
    readOnly: PropTypes.bool,
    type: PropTypes.string,
    fontSize: PropTypes.number,
    linkDashboardId: PropTypes.string,
    linkTabId: PropTypes.string,
  }),
  selected: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  id: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default BaseNode;
